/* eslint-disable no-console */
/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import PropTypes from "prop-types";
import { useState, useRef } from "react";

// react-router-dom components
import { Link, useNavigate } from "react-router-dom";
import { connect } from "react-redux";

// @mui material components
import Card from "@mui/material/Card";
// import Checkbox from "@mui/material/Checkbox";
// import { Field, reduxForm, formValueSelector } from "redux-form";
import { Field, reduxForm } from "redux-form";
import FormField from "components/FormField";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  getIdToken,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { getDatabase, ref, get } from "firebase/database";
import { useTranslation } from "react-i18next";
import { sendCreateMainUser, sendCheckCaptcha } from "store/actions/clientsActions";
import { newAlertMessage } from "store/actions/globalAlertActions";
import ReCAPTCHA from "react-google-recaptcha";

// Authentication layout components
import CoverLayout from "layouts/authentication/components/CoverLayout";

// import { CheckBox } from "@mui/icons-material";

// Initialize Firebase
import appConfig from "../../../../utils/appConfig";

const firebaseApp = initializeApp(appConfig);

const auth = getAuth(firebaseApp);
const realtimeDb = getDatabase(firebaseApp);
// eslint-disable-next-line no-useless-escape
const passwordValidation = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d!-\/:-@[-`{-~]{8,}$/;

const validate = (values) => {
  const errors = {};
  const requiredFields = ["firstName", "lastName", "email", "company", "password1", "password2"];
  requiredFields.forEach((field) => {
    if (!values[field]) {
      errors[field] = "Required";
    }
  });
  if (values.email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
    errors.email = "Invalid email address";
  }

  if (values.password1 !== values.password2) {
    errors.password1 = "Passwords are different";
    errors.password2 = "Passwords are different";
  }
  return errors;
};

function CoverForm(props) {
  const {
    pristine,
    handleSubmit,
    sendCreateMainUserAction,
    sendCheckCaptchaAction,
    actNewAlertMessage,
  } = props;

  const [message, setMessage] = useState("");
  const { t } = useTranslation();
  const captchaRef = useRef(null);
  const navigate = useNavigate();
  const signUp = async (data) => {
    if (data) {
      const { password1, email } = data;
      if (passwordValidation.test(password1)) {
        const normalizedEmail = String(email).trim().toLowerCase();
        try {
          let user = null;

          try {
            const { user: createdUser } = await createUserWithEmailAndPassword(
              auth,
              normalizedEmail,
              password1
            );
            user = createdUser;
          } catch (createError) {
            if (createError?.code === "auth/email-already-in-use") {
              try {
                const { user: existingUser } = await signInWithEmailAndPassword(
                  auth,
                  normalizedEmail,
                  password1
                );
                user = existingUser;
              } catch (e) {
                console.log("Error signing in existing user after email already in use error:", e);

                if (
                  e?.code === "auth/wrong-password" ||
                  e?.code === "auth/invalid-login-credentials"
                ) {
                  actNewAlertMessage({
                    type: "error",
                    title: t("password"),
                    content: t("mustUseExistingPassword"),
                  });
                  return;
                }
              }
            } else {
              throw createError;
            }
          }

          const existingMainUserDataSnap = await get(
            ref(realtimeDb, `/users/${user.uid}/mainUserData`)
          );

          const existingMainUserData = existingMainUserDataSnap.exists()
            ? existingMainUserDataSnap.val()
            : null;

          const isAlreadyMainUser = existingMainUserData !== null;

          if (isAlreadyMainUser) {
            navigate("/authentication/sign-in/cover", { replace: true });
            return;
          }

          const token = await getIdToken(user);
          if (user?.uid && token) {
            sendCreateMainUserAction({
              uid: user?.uid,
              email: normalizedEmail,
              token,
              ...data,
              cb: (response) => {
                const isSuccess = response === true || response?.ok === true;
                const isUserAlreadyProjectManager = response?.error === "userIsProjectManager";

                if (isSuccess) {
                  navigate("/authentication/sign-up/validationPending");
                  return;
                }

                if (isUserAlreadyProjectManager) {
                  const translatedMessage = t("cannotRegisterMainUserAlreadyProjectManager");
                  actNewAlertMessage({
                    type: "error",
                    title: t("signUp"),
                    content: translatedMessage,
                  });
                  setMessage(translatedMessage);
                  return;
                }

                setMessage(t("unknownError"));
              },
            });
          }
          setMessage("");
        } catch (error) {
          console.log("Error during sign up/sign in:", error);
          switch (error.code) {
            case "auth/invalid-email":
              setMessage(t("invalidEmail"));
              break;

            case "auth/weak-password":
              setMessage(t("weakPassword"));
              break;

            default:
              break;
          }
        }
      } else {
        setMessage(t("weakPassword1"));
      }
    }
  };

  const onSubmit = (data) => {
    if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
      signUp(data);
    } else {
      const token = captchaRef.current.getValue();
      sendCheckCaptchaAction(token).then((response) => {
        if (Object.hasOwnProperty.call(response, "success")) {
          const { success } = response;
          if (success === true) signUp(data);
        }
      });
      captchaRef.current.reset();
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <CoverLayout>
        <Card>
          <MDBox
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="success"
            mx={2}
            mt={-3}
            p={3}
            mb={1}
            textAlign="center"
          >
            <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
              {t("joinUsToday")}
            </MDTypography>
            <MDTypography display="block" variant="button" color="white" my={1}>
              {t("enterEmailAndPassword")}
            </MDTypography>
          </MDBox>
          <MDBox pt={4} pb={3} px={3}>
            <MDBox role="form">
              <MDBox mb={2}>
                <Field name="firstName" component={FormField} label={t("First Name")} />
              </MDBox>
              <MDBox mb={2}>
                <Field name="lastName" component={FormField} label={t("Last Name")} />
              </MDBox>
              <MDBox mb={2}>
                <Field name="company" component={FormField} label={t("Company")} />
              </MDBox>
              <MDBox mb={2}>
                <Field name="email" component={FormField} label={t("Email")} />
              </MDBox>
              <MDBox mb={2}>
                <Field name="password1" component={FormField} label={t("Password")} />
              </MDBox>
              <MDBox mb={2}>
                <Field name="password2" component={FormField} label={t("PasswordRepeat")} />
              </MDBox>
              <ReCAPTCHA sitekey={process.env.REACT_APP_SITE_KEY} ref={captchaRef} />
              <MDBox mt={4} mb={1}>
                <MDButton
                  type="submit"
                  variant="gradient"
                  size="small"
                  disabled={pristine}
                  color="info"
                  fullWidth
                >
                  {t("signUp")}
                </MDButton>
              </MDBox>
              <div style={{ fontSize: "0.75rem", fontWeight: "300", color: "#F44335" }}>
                {message}
              </div>
              <MDBox mt={3} mb={1} textAlign="center">
                <MDTypography variant="button" color="text">
                  {`${t("alreadyHaveAccount")} `}
                  <MDTypography
                    component={Link}
                    to="/authentication/sign-in/cover"
                    variant="button"
                    color="info"
                    fontWeight="medium"
                    textGradient
                  >
                    {t("signIn")}
                  </MDTypography>
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        </Card>
      </CoverLayout>
    </form>
  );
}

CoverForm.defaultProps = {};

CoverForm.propTypes = {
  actNewAlertMessage: PropTypes.func.isRequired,
  sendCheckCaptchaAction: PropTypes.func.isRequired,
  sendCreateMainUserAction: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  actNewAlertMessage: (message) => dispatch(newAlertMessage(message)),
  sendCreateMainUserAction: (data) => dispatch(sendCreateMainUser(data)),
  sendCheckCaptchaAction: (token) =>
    new Promise((resolve, reject) => {
      dispatch(sendCheckCaptcha({ token, resolve, reject }));
    }),
});

export default connect(
  null,
  mapDispatchToProps
)(
  reduxForm({
    form: "CoverForm", // a unique identifier for this form
    validate,
  })(CoverForm)
);
